import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NavIcon } from 'shared/components/Icons';
import { Tooltip } from 'shared/components/Tooltip';
import { activeGroupId, visibleNavGroups } from 'layout/navigation';

export function Sidebar({
  t,
  dir,
  hasPermission,
  collapsed,
  desktop,
  mobileOpen,
  onNavigate,
  onClose,
  closeLabel,
  brand,
  menuLabel,
}) {
  const location = useLocation();
  const groups = useMemo(() => visibleNavGroups(hasPermission), [hasPermission]);
  const currentGroup = activeGroupId(groups, location.pathname);
  const [openGroups, setOpenGroups] = useState(() => (currentGroup ? { [currentGroup]: true } : {}));

  useEffect(() => {
    if (!currentGroup) {
      return;
    }
    setOpenGroups((current) => (current[currentGroup] ? current : { ...current, [currentGroup]: true }));
  }, [currentGroup]);

  const toggleGroup = (id) => {
    setOpenGroups((current) => ({ ...current, [id]: !current[id] }));
  };

  const tooltipSide = dir === 'rtl' ? 'start' : 'end';

  return (
    <aside
      className={[
        'sidebar',
        collapsed ? 'is-collapsed' : '',
        mobileOpen ? 'is-open' : '',
      ].filter(Boolean).join(' ')}
      aria-label={menuLabel}
      aria-hidden={!desktop && !mobileOpen ? true : undefined}
      data-testid="app-sidebar"
    >
      <div className="sidebar-brand">
        <span className="sidebar-brand-mark">{brand}</span>
        {!desktop ? (
          <button type="button" className="icon-btn touch-target" onClick={onClose} aria-label={closeLabel}>
            <NavIcon name="close" />
          </button>
        ) : null}
      </div>
      <nav className="sidebar-nav" aria-label={menuLabel}>
        {groups.map((group) => {
          const expanded = collapsed || openGroups[group.id] !== false;
          return (
            <div key={group.id} className="nav-group">
              {!collapsed ? (
                <button
                  type="button"
                  className="nav-group-toggle"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={expanded}
                >
                  <span>{t(group.labelKey)}</span>
                  <NavIcon name="chevron" className={expanded ? 'nav-icon is-open' : 'nav-icon'} />
                </button>
              ) : (
                <div className="nav-group-divider" aria-hidden="true" />
              )}
              {expanded ? (
                <div className="nav-group-items">
                  {group.items.map((item) => {
                    const active = item.isActive(location.pathname);
                    const link = (
                      <NavLink
                        to={item.to}
                        className={() => (active ? 'nav-link is-active' : 'nav-link')}
                        aria-current={active ? 'page' : undefined}
                        onClick={onNavigate}
                      >
                        <NavIcon name={item.icon} />
                        <span className="nav-link-label">{t(item.key)}</span>
                      </NavLink>
                    );
                    return (
                      <div key={item.to}>
                        {collapsed ? (
                          <Tooltip label={t(item.key)} side={tooltipSide}>{link}</Tooltip>
                        ) : link}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
